import { LitElement, html, ifDefined, styles, defineElement } from "@mds/core";
import { selectCSS } from "./select.styles.js";

/**
 * Layout Web Component
 * @slot - This element has a slot
 */
export class MdsSelect extends LitElement {
	static get formAssociated() {
		return true;
	}

	static get styles() {
		return [...styles, selectCSS];
	}

	static get properties() {
		return {
			label: { type: String, reflect: true },
			ariaLabel: { type: String, attribute: "aria-label", reflect: true },
			id: { type: String, reflect: true },
			name: { type: String, reflect: true },
			position: { type: String, reflect: true },
			value: { type: String, reflect: true },
			errormessage: { type: String, reflect: true },
			message: { type: String, reflect: true },
			invalid: { type: Boolean, reflect: true },
			disabled: { type: Boolean, reflect: true },
			required: { type: Boolean, reflect: true },
			native: { type: Boolean, reflect: true },
			ondark: { type: Boolean, reflect: true }
		};
	}

	constructor() {
		super();
		this.internals = this.attachInternals();
		this.id = "select";
		this.name = "select";
		this.position = "top";
		this.label = undefined;
		this.ariaLabel = undefined;
		this.message = undefined;
		this.errormessage = undefined;
		this.value = "";
		this.invalid = false;
		this.disabled = false;
		this.required = false;
		this.native = false;
		this.addEventListener("click", event => {
			this.click();
		});
	}

	firstUpdated(_changedProperties) {
		super.firstUpdated(_changedProperties);
		this.internals.setFormValue(this.value);
	}

	__getOptions() {
		const slotContent = [...this.querySelectorAll("*")];
		const inValidSlotElements = slotContent.some(
			elm => !/\boption|optgroup\b/i.test(elm.tagName)
		);
		if (inValidSlotElements) {
			console.error(
				`Select elements may only contain <option> or <optgroup> tags`
			);
			return html``;
		} else {
			return document
				.createRange()
				.createContextualFragment(this.innerHTML);
		}
	}

	render() {
		return this.__selectTemplate();
	}

	__selectTemplate() {
		return html`
			<div class="mds-select">
				${this.__labelTemplate()}
				<select
					aria-label=${ifDefined(this.ariaLabel)}
					@change="${this.__changedselect}"
					id="${ifDefined(this.id)}"
					?disabled="${this.disabled}"
					?required="${this.required}"
					?ondark="${this.ondark}"
				>
					${this.__getOptions()}
				</select>
				${this.__getMessages()}
			</div>
		`;
	}

	__getMessages() {
		return this.message || this.invalid
			? html`
					<div class="messages">
						${this.__messageTemplate()}
						${this.__errorMessageTemplate()}
					</div>
			  `
			: html``;
	}

	__labelTemplate() {
		return this.label
			? html`
					<label for="${this.id}">${this.label}</label>
			  `
			: html``;
	}

	__errorMessageTemplate() {
		return this.invalid
			? html`
					<p class="error">${this.errormessage}</p>
			  `
			: html``;
	}

	__messageTemplate() {
		return this.message
			? html`
					<p class="message mds-body-small">${this.message}</p>
			  `
			: html``;
	}

	updated() {
		const options = this.shadowRoot.querySelector("#" + this.id).options;
		if (options.length > 0) {
			for (var i = 0; i < options.length; i++) {
				options[i].value === this.value
					? (options[i].selected = true)
					: (options[i].selected = false);
			}
		}
	}

	__changedselect(event) {
		if (!this.disabled) {
			this.value = event.target.value;
			this.internals.setFormValue(this.value);

			const changeEvent = new event.constructor(event.type, event);
			this.dispatchEvent(changeEvent);
		}

		if (this.native) {
			this.invalid = !event.target.checkValidity();
			if (!this.errormessage)
				this.errormessage = event.target.validationMessage;
			this.invalid
				? this.internals.setValidity(
						event.target.validity,
						this.errormessage
				  )
				: this.internals.setValidity({});
		}
	}
}

defineElement("mds-select", MdsSelect);
